import { ModelBase } from 'store/model-base';
import { HeroBannerInfo } from 'store/hero-banner/models';
import { LineBannerInfo } from 'store/line-banner/models';
import { Formatter } from 'core/services';

export class CmsActivity extends ModelBase {

    public static storeName: string = 'cmsActivity';
    public bannerTemplate: CmsActivityBannerTemplate[];
    public lineData: CmsActivityLineData;
    public currentCycle: CmsCurrentCycle;
    public segmentSwitch: CmsSegmentSwitch;
    public lineCardData: CmsLineCardData;
    public planOptions: CmsPlanOptionsFeatures;
    public changePending: CmsActivityChangePending;
    public cmsDescriptionText: string;
    public cmsBodyText: string;
    public errorBanner: CmsActivityErrorBanner;
    public globalTravelPass: CmsGlobalTravelPass;
    public manageUserLink: {
        text: string;
        url: string;
        cbmUrl: string;
    };
    public geminiInternationalServices: {
        header: string;
        subtitle: string;
        body: string;
    };

    public getHeroBannerHeading(template: CmsActivityBannerTemplate, bannerInfo: HeroBannerInfo): string {
        let header: string = template.header;

        if (!header || !bannerInfo) {
            return header || '';
        }

        if (bannerInfo.nickname && header.includes('[NICKNAME]')) {
            header = header.replace('[NICKNAME]', bannerInfo.nickname);
        }

        header = this.replaceCmsText(header, bannerInfo.header);

        return header;
    }

    public getHeroBannerDescription(template: CmsActivityBannerTemplate, bannerInfo: HeroBannerInfo): string {
        let subheader: string = template.subheader;

        if (!subheader || !bannerInfo) {
            return subheader || '';
        }

        if (bannerInfo.nickname && subheader.includes('[NICKNAME]')) {
            subheader = subheader.replace('[NICKNAME]', bannerInfo.nickname);
        }

        subheader = this.replaceCmsInDescription(subheader, bannerInfo);

        if (subheader.includes('[SCHEDULEDDATE]') || subheader.includes('[SCHEDULEDTIME]')) {
            return '';
        }

        return subheader;
    }

    public getHeroBannerBody(template: CmsActivityBannerTemplate, bannerInfo: HeroBannerInfo): string {
        let body: string = template.description;

        if (!body || !bannerInfo) {
            return body || '';
        }

        body = this.replaceCmsInBody(body, bannerInfo);

        return body;
    }

    public getBannerImageUrl(template: CmsActivityBannerTemplate): string {
        return template.image?.url;
    }

    public getCurrentHeroBanner(bannerInfo: HeroBannerInfo): CmsActivityBannerTemplate {
        const currentBanner: CmsActivityBannerTemplate = bannerInfo ? this.bannerTemplate.find((banner: CmsActivityBannerTemplate) => banner.id === bannerInfo.id) :
            this.bannerTemplate.find((banner: CmsActivityBannerTemplate) => banner.id === 'account.error');

        return currentBanner || this.defaultBanner;
    }

    public getCurrentLineBanner(bannerInfo: LineBannerInfo): CmsActivityBannerTemplate {
        return this.bannerTemplate.find((banner: CmsActivityBannerTemplate) => banner.id === bannerInfo.id);
    }

    public getLineBannerSubHeader(template: CmsActivityBannerTemplate, bannerInfo: LineBannerInfo): string {
        let subheader: string = template.subheader;

        if (!subheader || !bannerInfo) {
            return subheader || '';
        }

        if (bannerInfo.nickname && subheader.includes('[NICKNAME]')) {
            subheader = subheader.replace('[NICKNAME]', bannerInfo.nickname);
        }

        if (subheader.includes('[DATAUSED]')) {
            subheader = subheader.replace('[DATAUSED]', bannerInfo.subheader.dataUsed || '0');
        }

        subheader = this.replaceCmsText(subheader, bannerInfo.subheader);

        return subheader;
    }

    public getLineBannerDescription(template: CmsActivityBannerTemplate, bannerInfo: LineBannerInfo): string {
        let description: string = template.description;

        if (!description || !bannerInfo) {
            return description || '';
        }
        description = this.replaceCmsText(description, bannerInfo.description);

        return description;
    }

    public get getDefaultMemberViewerBanner(): CmsActivityBannerTemplate {
        return this.bannerTemplate.find((banner: CmsActivityBannerTemplate) => banner.id === 'account.lle.memberViewer');
    }

    private get defaultBanner(): CmsActivityBannerTemplate {
        return this.bannerTemplate.find((banner: CmsActivityBannerTemplate) => banner.id === 'account.default');
    }

    private replaceCmsText(cmsText: string, apiValue: Header): string {

        let tempCmsText: string = cmsText;
        Object.keys(apiValue).forEach((key: string) => {
            tempCmsText = tempCmsText.replace(`[${key.toUpperCase()}]`, apiValue[key]);
        });

        return tempCmsText;
    }

    private replaceCmsInDescription(cmsText: string, apiValue: HeroBannerInfo): string {

        this.cmsDescriptionText = cmsText;
        apiValue.subheader.rmaExpirationDate = Formatter.formatMonthDate(apiValue.subheader.rmaExpirationDate);
        apiValue.subheader.fmipDeadlineDate = Formatter.formatMonthDate(apiValue.subheader.fmipDeadlineDate);

        if (this.cmsDescriptionText.includes('[SCHEDULEDDATE]')) { 
            const scheduledDate: string = Formatter.formatMonthDay(apiValue.ctaButton.oddDeliveryStartTime);
            if (scheduledDate) {
                this.cmsDescriptionText = this.cmsDescriptionText.replace('[SCHEDULEDDATE]', scheduledDate);
            }
        }

        if (this.cmsDescriptionText.includes('[SCHEDULEDTIME]')) {
            const scheduledTime: string = Formatter.formatTimeRange(apiValue.ctaButton.oddDeliveryStartTime, apiValue.ctaButton.oddDeliveryEndTime);
            if (scheduledTime) {
                this.cmsDescriptionText = this.cmsDescriptionText.replace('[SCHEDULEDTIME]', scheduledTime);
            }
        }

        for (const obj in apiValue) {
            Object.keys(apiValue[obj]).forEach((key: string) => {
                this.cmsDescriptionText = this.cmsDescriptionText.replace(`[${key.toUpperCase()}]`, apiValue[obj][key]);
            });
        }

        return this.cmsDescriptionText;
    }

    private replaceCmsInBody(cmsText: string, apiValue: HeroBannerInfo): string {

        this.cmsBodyText = cmsText;
        const formattedDate: string = Formatter.formatDateToMDY(apiValue.ctaButton.promoQualificationExpiryDate);
        apiValue.ctaButton.promoQualificationExpiryDate = `**${formattedDate}**`;
        apiValue.ctaButton.currentPromoExpiryDt = Formatter.formatDateToMDY(apiValue.ctaButton.currentPromoExpiryDt);
        apiValue.ctaButton.currentPromoStartDate = Formatter.formatDateToMDY(apiValue.ctaButton.currentPromoStartDate);
        apiValue.ctaButton.currentPromoEndDate = Formatter.formatDateToMDY(apiValue.ctaButton.currentPromoEndDate);
        for (const obj in apiValue) {
            Object.keys(apiValue[obj]).forEach((key: string) => {
                this.cmsBodyText = this.cmsBodyText.replace(`[${key.toUpperCase()}]`, apiValue[obj][key]);
            });
        }

        return this.cmsBodyText;
    }
}
